import React from "react";
import type { MRT_Row, MRT_TableInstance } from "material-react-table";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import type { FeatureCollection } from "geojson";
import { GeoData } from "@rogoag/shared-ts";
import { Buffer } from "buffer";

import type { Job } from "../../../../models";
import type { IAirtableAttachment } from "@rogoag/airtable";
import { FileDownloader } from "../../../../utilities/downloadFile";
import { Oops } from "../../../../utilities/oops";
import type { DownloadMenuContext, FileMenuItem, GeoJSONDownloadFormat } from "../JobsDashboardTable.types";
import { FILE_MENU_LIST } from "../JobsDashboardTable.types";
import {
	generateCSV,
	getDownloadItemName,
	getDownloadRowName,
	getJobIdFromRow,
	getFilesForMenuItem,
	getAllFilesForMenuItems,
	getJobAttachmentsForMenuItem,
} from "../JobsDashboardTable.helpers";

async function convertGeoJSON(
	geoJSON: FeatureCollection,
	format: GeoJSONDownloadFormat,
	baseName: string,
): Promise<File> {
	switch (format) {
		case "kml":
			return GeoData.toKML_File(geoJSON, "GeoJSON", baseName);
		case "kmz":
			return GeoData.toKMZ_File(geoJSON, "GeoJSON", baseName);
		case "geojson":
			return GeoData.toGeoJSON_File(geoJSON, "GeoJSON", baseName);
		case "shapefile":
		default:
			return GeoData.toSHP_File(geoJSON, "GeoJSON", baseName);
	}
}

export interface UseFileDownloadsParams {
	jobs: Job[];
	selectedJobs: Job[];
	userEmail?: string;
	tableRef: React.RefObject<MRT_TableInstance<Job> | undefined>;
	actionMenuRow: MRT_Row<Job> | null;
}

export interface UseFileDownloadsReturn {
	downloadFiles: (
		jobIds: string[],
		files: IAirtableAttachment[],
		columnName: string,
		rowName: string,
		mergeCSV?: boolean,
	) => () => Promise<void>;
	downloadGeoInFormat: (
		fileMenuItem: FileMenuItem,
		format: GeoJSONDownloadFormat,
		rowName: string,
	) => () => Promise<void>;
	downloadMergedGeoInFormat: (
		fileMenuItem: FileMenuItem,
		format: GeoJSONDownloadFormat,
		rowName: string,
	) => () => Promise<void>;
	exportCSV: () => Promise<void>;
	copyJobId: () => void;
	getDownloadMenuContext: () => DownloadMenuContext;
}

export function useFileDownloads({
	jobs,
	selectedJobs,
	userEmail,
	tableRef,
	actionMenuRow,
}: UseFileDownloadsParams): UseFileDownloadsReturn {
	function downloadFiles(
		jobIds: string[],
		files: IAirtableAttachment[],
		columnName: string,
		rowName: string,
		mergeCSV: boolean = false,
	) {
		return async () => {
			const toastDownloading = toast.loading("Downloading files...");

			try {
				await FileDownloader.downloadAttachments(files, getFileName(), files.length > 1, mergeCSV);
				Sentry.logger.info(`Downloaded ${columnName} file(s)`, {
					airtable_record_id: jobIds.join(","),
					username: userEmail,
				});
			} catch (error) {
				Oops.handleError(error, "JobsDashboardTable.downloadFiles", userEmail);
			} finally {
				toast.dismiss(toastDownloading);
			}

			function getFileName() {
				let fileName = "";
				if (files.length > 1) {
					fileName = rowName ? `${rowName} - ${columnName}` : "";
				}
				return fileName;
			}
		};
	}

	async function exportCSV() {
		try {
			const table = tableRef.current;
			if (!table) return;

			const toastLoading = toast.loading("Generating CSV...");
			const rows = table.getFilteredRowModel().rows;
			const excludedColumns = ["Files", "Expand", "Select", "Map Issue"];
			const columns = table.getAllColumns().filter((col) => !excludedColumns.includes(col.columnDef.header));

			const csv = generateCSV(columns, rows);

			const blob = new Blob([csv], { type: "text/csv" });
			const filename = `rogo_jobs_export_${new Date().toISOString().split("T")[0]}.csv`;
			FileDownloader.downloadBlob(blob, filename);

			toast.dismiss(toastLoading);
			toast.success(`Exported ${rows.length} rows to CSV`);

			Sentry.logger.info("CSV export from Jobs Dashboard", {
				username: userEmail,
				airtable_record_count: jobs.length,
				airtable_record_id: jobs.map((job) => job.id).join(","),
			});
		} catch (error) {
			Oops.handleError(error, "JobsDashboardTable.exportCSV", userEmail);
		}
	}

	function downloadGeoInFormat(fileMenuItem: FileMenuItem, format: GeoJSONDownloadFormat, rowName: string) {
		return async () => {
			const toastDownloading = toast.loading("Converting and downloading...");
			try {
				const jobAttachments = getJobAttachmentsForMenuItem(fileMenuItem, actionMenuRow, selectedJobs, jobs);
				if (jobAttachments.length === 0) {
					toast.error("No files found");
					return;
				}

				// Shapefile shortcut: files are already SHP, no conversion needed
				if (format === "shapefile") {
					const allFiles = jobAttachments.flatMap((ja) => ja.attachments);
					const fileName = allFiles.length > 1 ? `${rowName} - ${fileMenuItem.label}` : "";
					await FileDownloader.downloadAttachments(allFiles, fileName, allFiles.length > 1);
					return;
				}

				// Convert each attachment to the requested format
				const convertedFiles: File[] = [];
				for (const { job, attachments } of jobAttachments) {
					for (const attachment of attachments) {
						const buffer = await FileDownloader.loadAttachment<ArrayBuffer>(attachment, job.id, "arraybuffer");
						if (!buffer) continue;
						const geoJSON = await GeoData.toGeoJSON(Buffer.from(buffer), "SHP");
						const baseName = attachment.filename.replace(/\.[^.]+$/, "");
						const file = await convertGeoJSON(geoJSON as FeatureCollection, format, baseName);
						convertedFiles.push(file);
					}
				}

				if (convertedFiles.length === 0) {
					toast.error("Failed to convert files");
					return;
				}

				const zipName = convertedFiles.length > 1 ? `${rowName} - ${fileMenuItem.label}` : "";
				await FileDownloader.downloadFiles(convertedFiles, zipName, convertedFiles.length > 1);

				Sentry.logger.info(`Downloaded ${fileMenuItem.label} as ${format}`, { username: userEmail });
			} catch (error) {
				Oops.handleError(error, "JobsDashboardTable.downloadGeoInFormat", userEmail);
			} finally {
				toast.dismiss(toastDownloading);
			}
		};
	}

	function downloadMergedGeoInFormat(fileMenuItem: FileMenuItem, format: GeoJSONDownloadFormat, rowName: string) {
		return async () => {
			const toastDownloading = toast.loading("Merging and downloading...");
			try {
				const jobAttachments = getJobAttachmentsForMenuItem(fileMenuItem, actionMenuRow, selectedJobs, jobs);
				if (jobAttachments.length === 0) {
					toast.error("No files found");
					return;
				}

				// Load all attachments and convert to GeoJSON
				const geoJSONs: FeatureCollection[] = [];
				for (const { job, attachments } of jobAttachments) {
					for (const attachment of attachments) {
						const buffer = await FileDownloader.loadAttachment<ArrayBuffer>(attachment, job.id, "arraybuffer");
						if (!buffer) continue;
						const geoJSON = await GeoData.toGeoJSON(Buffer.from(buffer), "SHP");
						if (geoJSON) geoJSONs.push(geoJSON as FeatureCollection);
					}
				}

				if (geoJSONs.length === 0) {
					toast.error("Failed to load files for merging");
					return;
				}

				const merged = GeoData.merge(geoJSONs);
				const baseName = `${rowName} - ${fileMenuItem.label} (Merged)`;
				const file = await convertGeoJSON(merged, format, baseName);
				await FileDownloader.downloadFile(file);

				Sentry.logger.info(`Downloaded merged ${fileMenuItem.label} as ${format}`, { username: userEmail });
			} catch (error) {
				Oops.handleError(error, "JobsDashboardTable.downloadMergedGeoInFormat", userEmail);
			} finally {
				toast.dismiss(toastDownloading);
			}
		};
	}

	function copyJobId() {
		const jobId = getJobIdFromRow(actionMenuRow, selectedJobs);
		navigator.clipboard
			.writeText(jobId)
			.then(() => {
				toast.success("Job ID copied to clipboard");
			})
			.catch(() => {
				toast.error("Failed to copy Job ID to clipboard");
			});
	}

	function getDownloadMenuContext(): DownloadMenuContext {
		const itemName = getDownloadItemName(actionMenuRow, selectedJobs);
		const jobId = getJobIdFromRow(actionMenuRow, selectedJobs);
		const allFiles = getAllFilesForMenuItems(FILE_MENU_LIST, actionMenuRow, selectedJobs, jobs);

		const fileItems = FILE_MENU_LIST.map((menuItem) => {
			const files = getFilesForMenuItem(menuItem, actionMenuRow, selectedJobs, jobs);
			const rowName = getDownloadRowName(actionMenuRow, selectedJobs);
			return { menuItem, files, rowName };
		});

		const showMergedCSV = !actionMenuRow || actionMenuRow?.getIsGrouped();
		const mergedCSVFiles = getFilesForMenuItem(
			{ label: "Sampling CSV", property: "labCSV" },
			actionMenuRow,
			selectedJobs,
			jobs,
		);

		const isGroupRow = !actionMenuRow ? selectedJobs.length > 0 || jobs.length > 0 : actionMenuRow.getIsGrouped();

		return { itemName, jobId, allFiles, fileItems, showMergedCSV, mergedCSVFiles, isGroupRow };
	}

	return {
		downloadFiles,
		downloadGeoInFormat,
		downloadMergedGeoInFormat,
		exportCSV,
		copyJobId,
		getDownloadMenuContext,
	};
}
